import { Modal } from 'Modal';
import { memo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'Button';
import { Text } from 'Text';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getSupportEmailAddress } from 'Program';
import { getUserProgramState } from 'Users';
import { InlineLink } from 'InlineLink';
import { useSelector } from 'react-redux';
import { useWeeklyVoiceTimeLimitState } from 'TutorBotConversation';
import './locales/TutorBotVoiceAgent/ShowModalOnVoiceTimeUsedUp-en.json';
import { getRoomIsOpen } from '../../redux/voiceAgent/voiceAgentSelectors';

/*
    ShowModalOnVoiceTimeUsedUp is a component that sits invisibly on the screen and checks to see if
    the user reaches the weekly limit on voice mode minutes. When that happens, a modal is triggered.

*/

// exported for stories
export const VoiceTimeUsedUpModal: FC<{
    allowedMinutes: number;
    refreshDayOfWeek: string;
    refreshHour: string;
}> = ({ allowedMinutes, refreshDayOfWeek, refreshHour }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotVoiceAgent.ShowModalOnVoiceTimeUsedUp' });
    const currentUser = useCurrentUser();
    const programType = getUserProgramState(currentUser)?.programType;
    const feedbackEmail = programType ? getSupportEmailAddress(programType, 'feedback') : 'feedback@quantic.edu';

    return (
        <Modal title={t('timeLimitReached')}>
            {({ closeModal }) => (
                <div className="px-7 pb-7">
                    <Text className="mb-4">
                        <p>{t('thanksForExploring', { allowedMinutes, refreshDayOfWeek, refreshHour })}</p>
                        <p>
                            {t('weAreRefining', {
                                feedbackEmail: (
                                    <InlineLink href={`mailto:${feedbackEmail}`}>{feedbackEmail}</InlineLink>
                                ),
                            })}
                        </p>
                    </Text>

                    <Button onPress={closeModal} fullWidth color="black">
                        {t('dismiss')}
                    </Button>
                </div>
            )}
        </Modal>
    );
};

const ShowModalOnVoiceTimeUsedUpComponent: FC = () => {
    const { overTimeLimit, refreshDayOfWeek, refreshHour, allowedMinutes } = useWeeklyVoiceTimeLimitState();
    const initialOverTimeLimit = useState(overTimeLimit)[0];
    // We don't want to show the modal while someone is in the voice conversation. We wait until they come out
    const roomIsOpen = useSelector(getRoomIsOpen());

    const modal =
        overTimeLimit && initialOverTimeLimit === false && !roomIsOpen ? (
            <VoiceTimeUsedUpModal
                refreshDayOfWeek={refreshDayOfWeek}
                refreshHour={refreshHour}
                allowedMinutes={allowedMinutes}
            />
        ) : null;

    return modal;
};

export const ShowModalOnVoiceTimeUsedUp = memo(
    ShowModalOnVoiceTimeUsedUpComponent,
) as typeof ShowModalOnVoiceTimeUsedUpComponent;
